import { Head, router, usePage } from "@inertiajs/react";
import { AlertCircle } from "lucide-react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import { DataTable } from "@/lib/data-table";
import { organizationApprovalsPath } from "@/routes";
import type { BreadcrumbItem, PagyMetadata, RansackFilters, SharedData } from "@/types";

import { columns } from "./columns";
import type { ApprovalsRow } from "./columns";

interface ApprovalsIndexProps {
  data: {
    purchase_requests: ApprovalsRow[];
    pagy: PagyMetadata;
  };
  filters: RansackFilters;
}

export default function ApprovalsIndex({ data, filters }: ApprovalsIndexProps) {
  const { purchase_requests, pagy } = data;
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";

  const approvalsPath = organizationApprovalsPath(organizationSlug);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Solicitudes por aprobar",
      href: approvalsPath,
    },
  ];

  const {
    isLoading,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({ filters, pagy, path: approvalsPath });

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Mis solicitudes por aprobar" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <Alert className="border-yellow-600 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-900">
            <strong className="font-semibold">Código de colores:</strong> Las filas resaltadas en{" "}
            <span className="inline-block rounded bg-yellow-100 px-2 py-0.5 font-medium">amarillo</span> son prioridad —
            sos el próximo en decidir (aprobar o rechazar) en el nivel actual de aprobación.
            El resto de las solicitudes te incluyen en niveles posteriores, por lo que no requieren acción inmediata.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Mis solicitudes por aprobar</h2>
          <p className="text-muted-foreground">
            Esta vista muestra únicamente las solicitudes de compra en aprobación en curso en las que participás como aprobador, en cualquier nivel del flujo.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Total: {pagy.count} solicitudes</div>
        </div>

        <div className={isLoading ? "opacity-50" : ""}>
          <DataTable
            columns={columns}
            data={purchase_requests}
            sorting={getSortingState()}
            onSortingChange={handleSortingChange}
            manualSorting={true}
            manualPagination={true}
            pageCount={pagy.pages}
            pagination={false}
            rowClassName={(row) =>
              row.assigned_to_current_user ? "bg-yellow-100" : undefined
            }
            onRowClick={(row) => router.visit(routes.purchaseRequests.show(row.id))}
          />
        </div>

        <DataTablePagination
          pagy={pagy}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          entityName="solicitudes"
        />
      </div>
    </AppLayout>
  );
}
